import { MutableRefObject, useMemo } from 'react'
import { GridReadyEvent } from 'ag-grid-community'
import { VersionReportVersionPageEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { ReorderRow } from '../../../../../applications/ports/versionReportVersionRepository'
import { ProgressOverview } from './ProgressOverview'
import { PageHeader, PageRoot } from '.'
import { ProgressReportByScope } from './ProgressReportByScope'
import { ProgressReportByTeam } from './ProgressReportByTeam'
import { VersionReportVersionPageFormModel } from '../dialog/VersionConfigDialog/model/VersionReportVersionPageFormModel'
import { StoredVersionReportPageDisplayState } from '../../hooks/pageDisplayState'
import { PendingEdits } from '../../hooks/currentReport'
import { EvaluateResult } from '../../../../../domain/entity/version-report/evaluation'

type VersionReportPageInput =
  | VersionReportVersionPageEntity
  | VersionReportVersionPageFormModel

type VersionReportPageProps = {
  snapshotUuid: string | undefined
  page: VersionReportPageInput | undefined
  takenAt?: Date
  updatePageTitle: (v: string) => void
  updateLeadSentence: (v: string) => void
  displayState: StoredVersionReportPageDisplayState
  updateDisplayState: (
    updated: Partial<StoredVersionReportPageDisplayState>
  ) => void
  updateRow: (
    rowUuid: string,
    lockVersion: number,
    key: 'status' | 'comment',
    value: string
  ) => Promise<boolean>
  applyReorderedRows: (
    pageUuid: string,
    reorderPayload: ReorderRow[]
  ) => Promise<void>
  commitPageContent: (
    pageUuid: string,
    overrides?: { pageTitle?: string; leadSentence?: string | undefined }
  ) => Promise<boolean>
  isPreview?: boolean
  showLoading: () => void
  hideLoading: () => void
  // Redis 復元時のみ増えるトークン。PROCESS の columnState 再適用トリガーに使う。
  displayStateRestoreToken?: number
  pendingEditsRef: MutableRefObject<PendingEdits>
  discardToken: number
  onGridReady?: (e: GridReadyEvent) => void
  onEvaluateRow?: (targetUuid: string) => Promise<EvaluateResult | undefined>
}
export const VersionReportPage = ({
  snapshotUuid,
  page,
  takenAt,
  isPreview,
  updatePageTitle,
  displayState,
  updateDisplayState,
  updateLeadSentence,
  updateRow,
  applyReorderedRows,
  commitPageContent,
  showLoading,
  hideLoading,
  displayStateRestoreToken,
  pendingEditsRef,
  discardToken,
  onGridReady,
  onEvaluateRow,
}: VersionReportPageProps) => {
  const pageUuid = page && 'uuid' in page ? page.uuid : null
  const onBlurPageTitle = (v: string): Promise<boolean> => {
    updatePageTitle(v)
    if (!pageUuid) return Promise.resolve(false)
    return commitPageContent(pageUuid, { pageTitle: v })
  }
  const onBlurLeadSentence = (v: string): Promise<boolean> => {
    updateLeadSentence(v)
    if (!pageUuid) return Promise.resolve(false)
    return commitPageContent(pageUuid, { leadSentence: v })
  }
  const component = useMemo(() => {
    if (!snapshotUuid || !page) return <></>
    if (page.config.type === 'PROGRESS_OVERVIEW') {
      return (
        <ProgressOverview
          editable={!isPreview}
          snapshotUuid={snapshotUuid}
          config={page.config}
          rows={page.rows}
          updateRow={updateRow}
          takenAt={takenAt}
          displayState={displayState}
          updateDisplayState={updateDisplayState}
          displayStateRestoreToken={displayStateRestoreToken}
          pendingEditsRef={pendingEditsRef}
          discardToken={discardToken}
          callbackWhenStartLoading={showLoading}
          callbackWhenFinishLoading={hideLoading}
        />
      )
    }
    if (
      page.config.type === 'PROGRESS_REPORT_BY_PROCESS' ||
      page.config.type === 'PROGRESS_REPORT_BY_TICKET_LIST'
    ) {
      return (
        <>
          <ProgressReportByScope
            key={page.uuid ?? ''}
            editable={!isPreview}
            snapshotUuid={snapshotUuid}
            pageUuid={page.uuid ?? ''}
            config={page.config}
            scope={
              page.config.type === 'PROGRESS_REPORT_BY_TICKET_LIST'
                ? 'TICKET_LIST'
                : 'PROCESS'
            }
            rows={page.rows}
            updateRow={updateRow}
            applyReorderedRows={applyReorderedRows}
            displayState={displayState}
            updateDisplayState={updateDisplayState}
            callbackWhenStartLoading={showLoading}
            callbackWhenFinishLoading={hideLoading}
            columnStateApplyToken={displayStateRestoreToken}
            pendingEditsRef={pendingEditsRef}
            discardToken={discardToken}
            onGridReady={onGridReady}
            onEvaluateRow={onEvaluateRow}
          />
        </>
      )
    }
    if (page.config.type === 'PROGRESS_REPORT_BY_TEAM') {
      return (
        <ProgressReportByTeam
          key={page.uuid ?? ''}
          editable={!isPreview}
          snapshotUuid={snapshotUuid}
          pageUuid={page.uuid ?? ''}
          config={page.config}
          rows={page.rows}
          updateRow={updateRow}
          applyReorderedRows={applyReorderedRows}
          displayState={displayState}
          updateDisplayState={updateDisplayState}
          callbackWhenStartLoading={showLoading}
          callbackWhenFinishLoading={hideLoading}
          columnStateApplyToken={displayStateRestoreToken}
          pendingEditsRef={pendingEditsRef}
          discardToken={discardToken}
          onGridReady={onGridReady}
          onEvaluateRow={onEvaluateRow}
        />
      )
    }
    return <></>
  }, [
    isPreview,
    page,
    snapshotUuid,
    takenAt,
    displayState,
    updateDisplayState,
    updateRow,
    applyReorderedRows,
    showLoading,
    hideLoading,
    displayStateRestoreToken,
    pendingEditsRef,
    discardToken,
    onGridReady,
    onEvaluateRow,
  ])
  return (
    <PageRoot>
      {page && (
        <PageHeader
          pageTitle={page.pageTitle}
          leadSentence={page.leadSentence}
          onChangePageTitle={updatePageTitle}
          onChangeLeadSentence={updateLeadSentence}
          onBlurPageTitle={onBlurPageTitle}
          onBlurLeadSentence={onBlurLeadSentence}
          editable={!isPreview}
        />
      )}
      {component}
    </PageRoot>
  )
}
